/**
 * Product roadmap (`/api/admin/v2/roadmap`), Features > Roadmap.
 * `system.config.view` reads, `system.config.edit` changes.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_GC_TIME } from '@/client/constants';
import { useHrApi } from '../hr/people';

const BASE = '/api/admin/v2/roadmap';

export type RoadmapStatus = 'shipped' | 'building' | 'planned' | 'parked';
export type Horizon = 'now' | 'next' | 'later';
export type Priority = 'P0' | 'P1' | 'P2';

export interface RoadmapItem {
  id: string;
  title: string;
  summary: string;
  area: string;
  status: RoadmapStatus;
  horizon: Horizon;
  quarter: string;
  progress?: number;
  owner: string;
  priority: Priority;
  /** Target / due date (ISO date) for calendar placement */
  targetDate: string | null;
  points: number;
  blockedBy?: string;
  dependsOn?: string[];
  /** When set, this initiative is nested under another board item */
  parentId?: string;
  /** Linked GitHub issues, cached by the last sync */
  githubIssues?: GithubIssue[];
  githubSyncedAt?: string | null;
}

export interface GithubIssue {
  /** "Org/repo#n" */
  ref: string;
  title?: string;
  state?: 'open' | 'closed';
  url?: string;
  closedAt?: string | null;
  error?: string;
}

export interface GithubSyncResult {
  items: number;
  issues: number;
  changed: number;
  errors: string[];
}

export interface MilestoneItem {
  id: string;
  title: string;
  date: string;
  kind: 'release' | 'review' | 'freeze';
}

/** Create or PATCH body; `null` clears an optional field. */
export type RoadmapItemInput = Omit<Partial<RoadmapItem>, 'progress' | 'githubIssues'> & {
  id?: string;
  progress?: number | null;
  /** Issue URLs or "repo#n" / "Org/repo#n" refs */
  githubIssues?: string[];
};

export const roadmapKeys = { all: ['roadmap'] as const };

export const useRoadmapQuery = () => {
  const api = useHrApi(BASE);
  return useQuery({
    queryKey: roadmapKeys.all,
    queryFn: () => api<{ items: RoadmapItem[]; milestones: MilestoneItem[] }>(''),
    staleTime: 15_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

function useRoadmapMutation<TIn, TOut>(fn: (api: ReturnType<typeof useHrApi>, input: TIn) => Promise<TOut>) {
  const api = useHrApi(BASE);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TIn) => fn(api, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: roadmapKeys.all }),
  });
}

export const useSaveRoadmapItemMutation = () =>
  useRoadmapMutation((api, { id, ...body }: RoadmapItemInput) =>
    id
      ? api<RoadmapItem>(`/items/${encodeURIComponent(id)}`, 'PATCH', body)
      : api<RoadmapItem>('/items', 'POST', body),
  );

export const useDeleteRoadmapItemMutation = () =>
  useRoadmapMutation((api, id: string) =>
    api<{ id: string; deleted: boolean }>(`/items/${encodeURIComponent(id)}`, 'DELETE'),
  );

/** Pull every linked issue from GitHub (read-only) and update progress/status. */
export const useSyncRoadmapGithubMutation = () =>
  useRoadmapMutation((api, _: void) => api<GithubSyncResult>('/github/sync', 'POST', {}));
